/* eslint-disable @1js/office-online-rules/no-v8-imports-for-fluentui */
import * as React from "react";

import type { IButtonStyles, ITheme } from "@fluentui/react";
import { concatStyleSets, memoizeFunction } from "@fluentui/react";

import { AppButton, getActionButtonStyles } from "@1js/acui-button";
import { isV9ButtonEnabled } from "@1js/acui-experiments";
import { AppThemeConsumer } from "@1js/acui-theme";
import { AppContext } from "@1js/acui-context";

import { animatePropsChanges } from "../../components/PropsAnimator/PropsAnimator";
import { getOnRenderText } from "../onRenderText";
import { RibbonButton as RibbonButtonNext } from "./next";
import type { RibbonButtonInternalProps } from "./RibbonButton.props";
import { getRibbonButtonLabelTransitionStyles } from "./RibbonButton.styles";
import { getHideLabelAnimationOptions } from "./RibbonButtonHideLabelAnimation";
import { useV9RibbonControlsEnabled } from "../../../../hooks";

declare let __Delay_FluentV8__: boolean;

const V8RibbonAppButton =
  typeof __Delay_FluentV8__ === "undefined" || !__Delay_FluentV8__
    ? AppButton
    : React.lazy(
        () =>
          import(
            /*webpackChunkName: "commonV8"*/ "@1js/acui-button/lib/AppButton"
          )
      );

const getStyles = memoizeFunction(
  (
    actionButtonStyles?: IButtonStyles,
    transitionStyles?: IButtonStyles,
    customStyles?: IButtonStyles
  ) => {
    return concatStyleSets(actionButtonStyles, transitionStyles, customStyles);
  }
);

class BaseRibbonButton extends React.PureComponent<
  RibbonButtonInternalProps,
  {}
> {
  private appButton = React.createRef<AppButton>();

  public render() {
    const transitionStyles = getRibbonButtonLabelTransitionStyles(
      this.props.textContainerInitialWidth,
      this.props.textContainerFinalWidth,
      this.props.hideLabel
    );

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <React.Suspense fallback={<></>}>
            <V8RibbonAppButton
              {...this.props}
              ref={this.appButton}
              styles={getStyles(
                getActionButtonStyles(theme),
                transitionStyles,
                this.props.styles
              )}
              onRenderText={
                this.props.onRenderText || getOnRenderText(this.props.size)
              }
              dataAutomationType={
                this.props.dataAutomationType || "RibbonButton"
              }
            />
          </React.Suspense>
        )}
      </AppThemeConsumer>
    );
  }
  public focus() {
    this.appButton && this.appButton.current && this.appButton.current.focus();
  }
}

const RibbonButtonCurrent = animatePropsChanges(
  getHideLabelAnimationOptions<RibbonButtonInternalProps>()
)(BaseRibbonButton);

RibbonButtonCurrent.displayName = "RibbonButtonCurrent";

export const RibbonButton = React.forwardRef<
  HTMLButtonElement,
  RibbonButtonInternalProps
>((props, ref) => {
  const appContext = React.useContext(AppContext);
  const isV9RibbonControlsEnabled = useV9RibbonControlsEnabled();
  const isNewButtonEnabled =
    (isV9ButtonEnabled(appContext.isExperimentalFeatureEnabled) ||
      isV9RibbonControlsEnabled) &&
    !props.useLegacyButton;
  return isNewButtonEnabled ? (
    <RibbonButtonNext ref={ref} {...props} />
  ) : (
    <RibbonButtonCurrent ref={ref as any} {...props} />
  );
});

RibbonButton.displayName = "RibbonButton";

export type RibbonButton = typeof RibbonButton.prototype;
